import { html, nothing } from "lit";
import { projectsForChatContext } from "../chat/fleet-projects-session.ts";
import {
  buildSessionDelegationGraph,
  findFleetDelegationRoot,
  type DelegationGraphNode,
  type SessionDelegationGraph,
} from "../chat/session-delegation-graph.ts";
import type { SessionSnapshot } from "../chat/session-snapshot.ts";
import {
  filterSubagentRunCards,
  subagentRunAgentLine,
  subagentRunTitle,
} from "../chat/subagent-run-display.ts";
import {
  resolveSubagentActivityPhase,
  subagentPhaseBadgeClass,
  subagentPhaseLabel,
} from "../chat/subagent-run-status.ts";
import type { FleetProjectSummary, SessionsListResult } from "../types.ts";
import type { ChatSubagentCard } from "../ui-types.ts";

export type FleetChatDelegationMapProps = {
  sessionKey: string;
  /**
   * Direct children (legacy callers). When `snapshot` is also supplied
   * the snapshot's children win — the two paths are deliberately
   * kept in sync by the snapshot invariant (UX-PLAN Pillar D).
   */
  subagents?: ChatSubagentCard[];
  /**
   * UX-PLAN Pillar D — single source of truth for session state.
   * When supplied, `snapshot.children` is used as the children list
   * and `snapshot.catalogHasSpawnedChildren` informs the empty state.
   */
  snapshot?: SessionSnapshot;
  sessions: SessionsListResult | null;
  projects?: FleetProjectSummary[];
  onOpenSubagentSession?: (params: { childKey: string; parentKey: string; label: string }) => void;
  onOpenProjectSession?: (project: FleetProjectSummary) => void;
  onEditRoutes?: () => void;
  /** Node ids that are currently expanded (for expand/collapse). Default: all expanded. */
  expandedNodeIds?: Set<string>;
  /** Called when user clicks expand/collapse toggle on a node. */
  onToggleNode?: (nodeId: string) => void;
};

function isActiveNode(node: DelegationGraphNode): boolean {
  // JAISIU-fleet-map-idle-demote — a node whose card has been
  // demoted to "idle" by `resolveSubagentActivityPhase` (typically
  // because it was restored from disk after a restart with no runId
  // and no fresh activity) must NEVER be considered active, even
  // though `node.status` still says "running" / "queued" / "spawning".
  if (node.idleDemoted) return false;
  return node.status === "running" || node.status === "spawning" || node.status === "queued";
}

/** Find the card for a node (by cardId or sessionKey). */
function findCardForNode(
  node: DelegationGraphNode,
  cards: ChatSubagentCard[],
): ChatSubagentCard | undefined {
  if (node.cardId) {
    return cards.find((c) => c.id === node.cardId);
  }
  if (node.sessionKey) {
    return cards.find((c) => c.childSessionKey === node.sessionKey);
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Streaming thoughts rendering (typewriter effect)
// ---------------------------------------------------------------------------
const STREAM_PREVIEW_LIMIT = 800; // chars shown in expanded node

function renderStreamingThoughts(card: ChatSubagentCard): ReturnType<typeof html> {
  const fullText = card.streamText ?? "";
  if (!fullText) return html``;

  // Show last N chars (most recent streaming content)
  const displayText = fullText.slice(-STREAM_PREVIEW_LIMIT);
  const revealed = card.typewriterIndex ?? displayText.length;
  const isAnimating = card.typewriterActive && revealed < displayText.length;

  return html`
    <div class="fleet-map-node__stream mono muted" aria-live="polite">
      ${
        isAnimating
          ? html`${displayText.slice(0, revealed)}<span class="typewriter-cursor" aria-hidden="true"
                >▌</span
              >`
          : displayText
      }
    </div>
  `;
}

function openSession(
  props: FleetChatDelegationMapProps,
  targetKey: string,
  parentKey: string,
  label: string,
) {
  if (!targetKey || !props.onOpenSubagentSession) {
    return;
  }
  props.onOpenSubagentSession({ childKey: targetKey, parentKey, label });
}

function renderNavButton(
  label: string,
  title: string,
  onClick: () => void,
  variant: "parent" | "child" | "open",
) {
  return html`
    <button
      type="button"
      class="fleet-map-node__btn fleet-map-node__btn--${variant}"
      title=${title}
      aria-label=${title}
      @click=${(e: Event) => {
        e.stopPropagation();
        onClick();
      }}
    >
      ${label}
    </button>
  `;
}

function renderNodeCard(
  node: DelegationGraphNode,
  graph: SessionDelegationGraph,
  props: FleetChatDelegationMapProps,
  cards: ChatSubagentCard[],
  expandedNodeIds: Set<string>,
) {
  const active = isActiveNode(node);
  const focused = node.sessionKey === graph.focusSessionKey;
  const parentKey = node.spawnedBy?.trim() ?? graph.fleetRootSessionKey;
  const childKeys = node.childSessionKeys.filter((k) => k.startsWith("agent:"));
  const canOpen =
    Boolean(node.sessionKey) &&
    (node.kind === "subagent" || node.kind === "parent") &&
    props.onOpenSubagentSession;
  const canOpenProject = node.kind === "project" && node.sessionKey && props.onOpenProjectSession;

  // Expand/collapse state: default to true if not specified
  const isExpanded = props.expandedNodeIds ? expandedNodeIds.has(node.id) : true;
  const hasToggle = node.kind === "subagent" || node.kind === "orchestration";
  const card = node.kind === "subagent" ? findCardForNode(node, cards) : undefined;
  const hasStream = Boolean(card?.streamText);

  return html`
    <div
      class="fleet-map-node fleet-map-node--${node.kind} ${active ? "fleet-map-node--active" : ""} ${focused ? "fleet-map-node--focus" : ""} ${!isExpanded ? "fleet-map-node--collapsed" : ""}"
      data-node-id=${node.id}
      role="group"
      aria-label=${`${node.label}, ${node.subtitle ?? node.kind}`}
      @click=${() => {
        if (hasToggle) return; // Don't navigate on toggle click
        if (canOpen && node.sessionKey) {
          openSession(props, node.sessionKey, parentKey, node.label);
        }
        if (canOpenProject && node.sessionKey) {
          const project = (props.projects ?? []).find((p) => p.sessionKey === node.sessionKey);
          if (project) {
            props.onOpenProjectSession?.(project);
          }
        }
      }}
    >
      <div class="fleet-map-node__header">
        <span class="fleet-map-node__title">${node.label}</span>
        <div class="fleet-map-node__header-right">
          ${
            // JAISIU-fleet-map-status + JAISIU-fleet-map-idle-demote —
            // always render the badge with an aria-label fallback. Three
            // branches:
            //   1. node.idleDemoted → surface the phase label ("Idle") +
            //      phase class so the Fleet Map agrees with the table,
            //      even though node.status still says "running" /
            //      "queued" / "spawning" on the wire.
            //   2. status === "done" → render "completed" (operator-facing
            //      wording).
            //   3. default → render raw status text.
            // Unknown statuses surface a clear "unknown" rather than
            // silently disappearing.
            (() => {
              if (node.idleDemoted && card) {
                const phase = resolveSubagentActivityPhase(card);
                const text = subagentPhaseLabel(phase);
                return html`<span
                  class="fleet-map-node__badge fleet-map-node__badge--${subagentPhaseBadgeClass(phase)}"
                  aria-label=${`Subagent status: ${text}`}
                  title=${`Subagent status: ${text}`}
                  >${text}</span
                >`;
              }
              const s = node.status ?? "unknown";
              const text = s === "done" ? "completed" : s;
              return html`<span
                class="fleet-map-node__badge fleet-map-node__badge--${s}"
                aria-label=${`Subagent status: ${text}`}
                title=${`Subagent status: ${text}`}
                >${text}</span
              >`;
            })()
          }
          ${
            hasToggle
              ? html`
                  <button
                    type="button"
                    class="fleet-map-node__toggle"
                    aria-label=${isExpanded ? "Collapse details" : "Expand details"}
                    aria-expanded=${isExpanded}
                    @click=${(e: Event) => {
                      e.stopPropagation();
                      props.onToggleNode?.(node.id);
                    }}
                  >
                    ${isExpanded ? "▲" : "▼"}
                  </button>
                `
              : nothing
          }
        </div>
      </div>
      <p class="fleet-map-node__meta">${node.subtitle ?? node.kind}</p>
      ${
        node.task?.trim()
          ? html`<p class="fleet-map-node__task">${node.task.trim().slice(0, 120)}</p>`
          : nothing
      }
      ${
        isExpanded && hasStream && card
          ? html`
              <div class="fleet-map-node__thoughts-label muted">💭 Streaming thoughts</div>
              ${renderStreamingThoughts(card)}
            `
          : nothing
      }
      ${
        isExpanded
          ? html`
              <div class="fleet-map-node__actions">
                ${
                  node.spawnedBy && props.onOpenSubagentSession
                    ? renderNavButton(
                        "↑ Parent",
                        "Open parent session",
                        () => {
                          openSession(props, node.spawnedBy!, graph.fleetRootSessionKey, "Parent");
                        },
                        "parent",
                      )
                    : nothing
                }
                ${
                  canOpen
                    ? renderNavButton(
                        "Open",
                        "Open this session tab",
                        () => {
                          if (node.sessionKey) {
                            openSession(props, node.sessionKey, parentKey, node.label);
                          }
                        },
                        "open",
                      )
                    : nothing
                }
                ${childKeys.slice(0, 4).map((childKey) => {
                  const childNode = graph.nodes.find((n) => n.sessionKey === childKey);
                  const childLabel = childNode?.label ?? childKey.split(":").slice(-2).join(":");
                  const short = childLabel.length > 22 ? `${childLabel.slice(0, 20)}…` : childLabel;
                  return renderNavButton(
                    `↓ ${short}`,
                    `Open child session ${childLabel}`,
                    () => {
                      openSession(
                        props,
                        childKey,
                        node.sessionKey ?? graph.fleetRootSessionKey,
                        childLabel,
                      );
                    },
                    "child",
                  );
                })}
                ${
                  childKeys.length > 4
                    ? html`<span class="fleet-map-node__more muted"
                        >+${childKeys.length - 4} more (open parent row)</span
                      >`
                    : nothing
                }
              </div>
            `
          : nothing
      }
    </div>
  `;
}

function renderTreeLevels(
  graph: SessionDelegationGraph,
  props: FleetChatDelegationMapProps,
  cards: ChatSubagentCard[],
) {
  if (graph.levels.length === 0) {
    return nothing;
  }
  const expandedNodeIds = props.expandedNodeIds ?? new Set<string>();
  return html`
    <div class="fleet-map-tree" role="tree" aria-label="Subagent delegation tree">
      ${graph.levels.map(
        (level, index) => html`
          <div class="fleet-map-tree__level" role="group" aria-label=${`Depth ${index}`}>
            <div class="fleet-map-tree__level-label muted">Level ${index}</div>
            <div class="fleet-map-tree__row">
              ${level.map((node) => renderNodeCard(node, graph, props, cards, expandedNodeIds))}
            </div>
          </div>
          ${
            index < graph.levels.length - 1
              ? html` <div class="fleet-map-tree__connector" aria-hidden="true"></div> `
              : nothing
          }
        `,
      )}
    </div>
  `;
}

function renderEdgeLegend(graph: SessionDelegationGraph) {
  const nested = graph.edges.filter((e) => e.nested).length;
  const active = graph.edges.filter((e) => e.active).length;
  return html`
    <div class="fleet-map-legend" aria-label="Map legend">
      <span class="fleet-map-legend__item fleet-map-legend__item--parent">Parent</span>
      <span class="fleet-map-legend__item fleet-map-legend__item--subagent">Subagent</span>
      <span class="fleet-map-legend__item fleet-map-legend__item--nested"
        >Nested spawn (${nested})</span
      >
      <span class="fleet-map-legend__item fleet-map-legend__item--active">Active (${active})</span>
      <span class="fleet-map-legend__item fleet-map-legend__item--focus">Current tab</span>
    </div>
  `;
}

function renderAssignmentTable(cards: ChatSubagentCard[], graph: SessionDelegationGraph) {
  if (cards.length === 0) {
    return nothing;
  }
  const sorted = [...cards].toSorted((a, b) => {
    const da = graph.nodes.find((n) => n.sessionKey === a.childSessionKey)?.depth ?? 0;
    const db = graph.nodes.find((n) => n.sessionKey === b.childSessionKey)?.depth ?? 0;
    return da - db || a.createdAt - b.createdAt;
  });
  return html`
    <table class="fleet-chat-delegation-map__table">
      <thead>
        <tr>
          <th scope="col">Depth</th>
          <th scope="col">Subagent task</th>
          <th scope="col">Agent</th>
          <th scope="col">Status</th>
          <th scope="col">Spawned by</th>
          <th scope="col">Session</th>
        </tr>
      </thead>
      <tbody>
        ${sorted.map((card) => {
          const node = graph.nodes.find((n) => n.sessionKey === card.childSessionKey);
          const spawnedBy = node?.spawnedBy ?? graph.fleetRootSessionKey;
          const parentNode = graph.nodes.find((n) => n.sessionKey === spawnedBy);
          return html`
            <tr>
              <td>${node?.depth ?? "—"}</td>
              <td>${subagentRunTitle(card)}</td>
              <td class="mono">${subagentRunAgentLine(card)}</td>
              <td>
                <span
                  class="fleet-map-node__badge fleet-map-node__badge--${subagentPhaseBadgeClass(resolveSubagentActivityPhase(card))}"
                >
                  ${subagentPhaseLabel(resolveSubagentActivityPhase(card))}
                </span>
              </td>
              <td class="mono muted">
                ${parentNode?.label ?? spawnedBy.split(":").slice(-2).join(":")}
              </td>
              <td class="mono muted">
                ${card.childSessionKey ? card.childSessionKey.split(":").slice(-2).join(":") : "spawning…"}
              </td>
            </tr>
          `;
        })}
      </tbody>
    </table>
  `;
}

export function renderFleetChatDelegationMap(props: FleetChatDelegationMapProps) {
  // UX-PLAN Pillar D — children come from the snapshot when present,
  // so the inspector and the thread cannot disagree about how many
  // children are alive. The legacy `subagents` prop is the fallback
  // for callers that haven't wired the snapshot yet.
  const rawChildren = props.snapshot?.children ?? props.subagents ?? [];
  const cards = filterSubagentRunCards(rawChildren);
  const fleetRoot = findFleetDelegationRoot(props.sessionKey, props.sessions?.sessions ?? []);
  const projects = projectsForChatContext(props.projects ?? [], fleetRoot);
  const graph = buildSessionDelegationGraph({
    sessionKey: props.sessionKey,
    subagents: cards,
    sessions: props.sessions,
    projects,
  });
  const hasSubagents = graph.nodes.some((n) => n.kind === "subagent");
  const hasTopology = hasSubagents || projects.length > 0 || graph.nodes.length > 1;

  // UX-PLAN Pillar D Gap 4 — empty state now distinguishes "nothing
  // yet" from "the catalog hasn't reported children". With the
  // snapshot, we know whether the catalog has any rows that point
  // at this session; without it we fall back to the topology check.
  const inspectorEmpty = props.snapshot
    ? !props.snapshot.catalogHasSpawnedChildren && !hasTopology
    : !hasTopology;

  return html`
    <div class="fleet-chat-delegation-map">
      <div class="fleet-chat-delegation-map__toolbar">
        <p class="muted fleet-chat-delegation-map__intro">
          <strong>Fleet map</strong> — delegation tree from
          <span class="mono">${graph.fleetRootSessionKey.split(":").slice(-2).join(":")}</span>.
          Nested subagents appear under whoever spawned them. Use <strong>↑ Parent</strong> /
          <strong>↓ Child</strong> to jump between sessions, or <strong>Open</strong> for the
          selected node.
        </p>
        ${
          props.onEditRoutes
            ? html`
                <button type="button" class="btn btn--sm" @click=${() => props.onEditRoutes?.()}>
                  Edit routes
                </button>
              `
            : nothing
        }
      </div>
      ${
        inspectorEmpty
          ? html`
              <p class="muted fleet-chat-delegation-map__empty">
                No projects or subagents yet. Spawn work from chat (@agent, @all, or sessions.spawn)
                to build a delegation tree here.
              </p>
            `
          : html`
              ${renderEdgeLegend(graph)} ${renderTreeLevels(graph, props, cards)}
              ${renderAssignmentTable(cards, graph)}
            `
      }
    </div>
  `;
}
